import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
} from '@mlm/ui';
import { listAudit, type AuditRow } from '../lib/api';

/**
 * Admin audit log viewer. Read-only — writes are owned by service code via
 * `writeAudit`. Filters apply server-side; pagination is per-page.
 */
export function AuditPage() {
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('');
  const [targetTableFilter, setTargetTableFilter] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['audit', page, actionFilter, targetTableFilter],
    queryFn: () =>
      listAudit({
        page,
        pageSize: 50,
        action: actionFilter || undefined,
        targetTable: targetTableFilter || undefined,
      }),
  });

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Audit Log</h1>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="action">
              Action (prefix)
            </label>
            <Input
              id="action"
              value={actionFilter}
              onChange={(e) => {
                setActionFilter(e.target.value);
                setPage(1);
              }}
              placeholder="e.g. payout, auth.login"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="target">
              Target table
            </label>
            <Input
              id="target"
              value={targetTableFilter}
              onChange={(e) => {
                setTargetTableFilter(e.target.value);
                setPage(1);
              }}
              placeholder="e.g. orders, payout_requests"
            />
          </div>
          <div className="flex items-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setActionFilter('');
                setTargetTableFilter('');
                setPage(1);
              }}
            >
              Clear filters
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Entries{' '}
            {query.data && (
              <span className="text-sm font-normal text-muted-foreground">
                ({query.data.total} total · page {page})
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {query.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (query.data?.rows.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">No audit entries match.</p>
          ) : (
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground border-b">
                  <tr>
                    <th className="py-2 pr-2">When</th>
                    <th className="py-2 pr-2">Actor</th>
                    <th className="py-2 pr-2">Action</th>
                    <th className="py-2 pr-2">Target</th>
                    <th className="py-2 pr-2">IP</th>
                    <th className="py-2 pr-2 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {query.data!.rows.map((r) => (
                    <Row
                      key={r.id}
                      r={r}
                      expanded={expanded === r.id}
                      onToggle={() => setExpanded(expanded === r.id ? null : r.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {query.data && query.data.total > query.data.pageSize && (
            <div className="flex items-center justify-end gap-2 pt-3">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Prev
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page * query.data.pageSize >= query.data.total}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Row({
  r,
  expanded,
  onToggle,
}: {
  r: AuditRow;
  expanded: boolean;
  onToggle: () => void;
}) {
  const when = new Date(r.createdAt);
  return (
    <>
      <tr className="border-b hover:bg-accent/40">
        <td className="py-2 pr-2 whitespace-nowrap">
          <span title={when.toISOString()}>{when.toLocaleString()}</span>
        </td>
        <td className="py-2 pr-2 max-w-xs truncate">
          {r.actorEmail ?? null}
        </td>
        <td className="py-2 pr-2 font-mono text-xs">{r.action}</td>
        <td className="py-2 pr-2 font-mono text-xs">
          {r.targetTable && (
            <>
              {r.targetTable}
              {r.targetId && <span className="text-muted-foreground">:{r.targetId.slice(0, 8)}</span>}
            </>
          )}
        </td>
        <td className="py-2 pr-2 text-xs text-muted-foreground">{r.ip ?? ''}</td>
        <td className="py-2 pr-2 text-right">
          <button onClick={onToggle} className="text-xs underline">
            {expanded ? 'hide' : 'detail'}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} className="py-2 pr-2 bg-muted/30">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
              <div>
                <div className="font-medium mb-1">Before</div>
                <pre className="bg-background p-2 rounded border overflow-auto">
{r.before ? JSON.stringify(r.before, null, 2) : '(none)'}
                </pre>
              </div>
              <div>
                <div className="font-medium mb-1">After</div>
                <pre className="bg-background p-2 rounded border overflow-auto">
{r.after ? JSON.stringify(r.after, null, 2) : '(none)'}
                </pre>
              </div>
            </div>
            {r.userAgent && (
              <div className="text-xs text-muted-foreground mt-2 break-all">
                UA: {r.userAgent}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
